import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BUILD_INFO } from '../BUILD_INFO.js';

/**
 * PWA install + update controller.
 *
 * Strict implementation of /manuals/pwa-install-update-spec.md §5 — refs
 * for non-reactive parts (registration / waiting / deferredPrompt /
 * updateRequested), useState only for things the UI re-renders on.
 *
 * Two signals drive `updateReady`:
 *
 *   1. `registration.waiting` — the browser fetched a byte-different sw.js
 *      and parked it in the `installed` state, waiting for SKIP_WAITING.
 *   2. Build mismatch — `serverBuild !== clientBuild` from /api/version
 *      polling. This is a fallback for the case where the SW lifecycle
 *      didn't fire `updatefound` (e.g. proxies, certain corporate browsers,
 *      or when the user just signed in before the SW polled).
 *
 * `update()` is a 3-step ladder: SKIP_WAITING the existing waiting → call
 * reg.update() and recheck waiting → controlled reload if the server
 * advertises a different build but no SW lifecycle event fired.
 */

const SW_URL = '/sw.js';
const POLL_INTERVAL_MS = 15_000;
const PHANTOM_GUARD_KEY = 'pwa-phantom-recovery';

// Strip optional leading "v" so "v1.0.2605070728" and "1.0.2605070728"
// compare as equal — see comment in PWAProvider.
function normaliseBuild(s) {
  return String(s || '').trim().replace(/^v/i, '');
}

const isStandalone = typeof window !== 'undefined' && (
  (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true
);

const PWAContext = createContext(null);

export function PWAProvider({ children }) {
  const [canInstall, setCanInstall] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [installed, setInstalled] = useState(isStandalone);
  const [serverBuild, setServerBuild] = useState(null);

  const deferredPromptRef = useRef(null);
  const registrationRef = useRef(null);
  const waitingRef = useRef(null);
  const updateRequestedRef = useRef(false);

  // BUILD_INFO.version is stored bare ("1.0.2605070728"); /api/version
  // returns the same stamp prefixed with "v" ("v1.0.2605070728"). Normalise
  // both ends to a single canonical form so the equality check is honest.
  // Without this normalisation the mismatch is *permanent* and "Update PWA"
  // would burn forever — that was the bug v1.0.2605070719..728 shipped with.
  const clientBuild = normaliseBuild(BUILD_INFO?.version);

  // Recompute updateReady from registration.waiting + serverBuild.
  const syncUpdateReady = useCallback(() => {
    const reg = registrationRef.current;
    const hasWaiting = !!(reg && reg.waiting);
    const buildMismatch = !!serverBuild && !!clientBuild && serverBuild !== clientBuild;
    const next = hasWaiting || buildMismatch;
    if (hasWaiting && reg.waiting) waitingRef.current = reg.waiting;
    // Console diagnostics — helps debug a stuck "Update PWA" in production
    // without needing to instrument from scratch each time.
    // eslint-disable-next-line no-console
    console.debug('[pwa] sync', {
      clientBuild, serverBuild, hasWaiting, buildMismatch, updateReady: next,
    });
    setUpdateReady(next);
  }, [serverBuild, clientBuild]);

  // ── /api/version polling ───────────────────────────────────────────
  // Cheap, no-auth endpoint. Every 15s we ask "what build does the server
  // think it is?" — the moment that diverges from clientBuild we know an
  // update is available even before the SW fetched the new sw.js.
  useEffect(() => {
    let disposed = false;
    let timer = null;

    async function fetchVersion() {
      try {
        const res = await fetch('/api/version', { credentials: 'same-origin', cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (disposed) return;
        const v = normaliseBuild(data?.version);
        if (v && v !== serverBuild) {
          setServerBuild(v);
          // Server moved — proactively poke the SW so its lifecycle catches up.
          if (registrationRef.current) {
            registrationRef.current.update().catch(() => { /* network blip */ });
          }
        }
      } catch { /* network blip — try again next tick */ }
    }
    fetchVersion();
    timer = setInterval(fetchVersion, POLL_INTERVAL_MS);
    return () => { disposed = true; if (timer) clearInterval(timer); };
  }, [serverBuild]);

  // ── beforeinstallprompt / appinstalled ─────────────────────────────
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };
    const onInstalled = () => {
      deferredPromptRef.current = null;
      setCanInstall(false);
      setInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // ── Service Worker registration + lifecycle ────────────────────────
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
    if (!import.meta.env.PROD) return undefined;       // skip in dev — collides with HMR

    let cancelled = false;
    let pollTimer = null;

    (async () => {
      // Phantom-controller protection: if the user clicked Unregister in
      // DevTools without reloading, navigator.serviceWorker.controller is
      // still truthy but getRegistrations() returns []. Any subsequent
      // register() bypasses the install/waiting states entirely → no
      // updatefound ever fires. One-shot reload (with sessionStorage
      // guard so we never loop) cleans the slate.
      try {
        if (navigator.serviceWorker.controller) {
          const existing = await navigator.serviceWorker.getRegistrations();
          if (existing.length === 0 && !sessionStorage.getItem(PHANTOM_GUARD_KEY)) {
            sessionStorage.setItem(PHANTOM_GUARD_KEY, '1');
            window.location.reload();
            return;
          }
        }
        sessionStorage.removeItem(PHANTOM_GUARD_KEY);
      } catch { /* getRegistrations may reject — proceed anyway */ }

      let reg;
      try { reg = await navigator.serviceWorker.register(SW_URL); }
      catch { return; }
      if (cancelled) return;
      registrationRef.current = reg;

      // A waiting worker may already exist when we arrive (background tab
      // updated us while we were elsewhere).
      if (reg.waiting && navigator.serviceWorker.controller) {
        waitingRef.current = reg.waiting;
        // eslint-disable-next-line no-console
        console.debug('[pwa] register: existing waiting worker found');
        setUpdateReady(true);
      }

      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        // eslint-disable-next-line no-console
        console.debug('[pwa] updatefound — new SW is installing');
        sw.addEventListener('statechange', () => {
          // eslint-disable-next-line no-console
          console.debug('[pwa] new SW statechange →', sw.state);
          if (sw.state === 'installed' && navigator.serviceWorker.controller) {
            waitingRef.current = sw;
            setUpdateReady(true);
          }
        });
      });

      // Periodic SW probe — 15s per spec §5 (was 30 min in v1, too slow).
      pollTimer = setInterval(() => { reg.update().catch(() => {}); }, POLL_INTERVAL_MS);
    })();

    // Re-probe on tab focus — common case: user came back from a meeting,
    // server has moved 3 times since.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && registrationRef.current) {
        registrationRef.current.update().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    // Reload only when the user explicitly asked (we set the ref before
    // calling SKIP_WAITING / location.reload).
    const onControllerChange = () => {
      if (!updateRequestedRef.current) return;
      updateRequestedRef.current = false;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  // Re-evaluate updateReady whenever serverBuild changes (or hasWaiting
  // could have flipped).
  useEffect(() => { syncUpdateReady(); }, [syncUpdateReady]);

  const install = useCallback(async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return false;
    prompt.prompt();
    const choice = await prompt.userChoice;
    deferredPromptRef.current = null;
    setCanInstall(false);
    return choice?.outcome === 'accepted';
  }, []);

  // Three-step Update PWA strategy — see spec §0.3.
  const update = useCallback(async () => {
    const reg = registrationRef.current;
    const hasNewerServer = !!serverBuild && !!clientBuild && serverBuild !== clientBuild;

    // Step 0: no SW registered (e.g. dev, or browser without SW support).
    if (!reg) {
      if (hasNewerServer) {
        updateRequestedRef.current = true;
        window.location.reload();
      }
      return;
    }

    // Step 1: a worker is already waiting → activate it.
    if (reg.waiting) {
      updateRequestedRef.current = true;
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      return;
    }

    // Step 2: poke the SW once and recheck.
    try { await reg.update(); } catch { /* network blip */ }
    if (reg.waiting) {
      updateRequestedRef.current = true;
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      return;
    }

    // Step 3: server says there's a new build but the SW hasn't caught up
    // yet — controlled reload. Reloading alone is not enough: the browser
    // may have a still-valid HTTP-cached /index.html from before our
    // no-cache headers landed. Force a network-only fetch to refresh both
    // browser HTTP cache and SW SHELL_CACHE before reloading.
    if (hasNewerServer) {
      updateRequestedRef.current = true;
      try {
        await fetch('/', { cache: 'reload', credentials: 'same-origin' });
      } catch { /* network blip — reload will surface the error */ }
      window.location.reload();
    }
  }, [serverBuild, clientBuild]);

  const value = useMemo(() => ({
    canInstall,
    updateReady,
    installed,
    install,
    update,
    clientBuild,
    serverBuild,
  }), [canInstall, updateReady, installed, install, update, clientBuild, serverBuild]);

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
}

export function usePWA() {
  const ctx = useContext(PWAContext);
  if (!ctx) {
    return {
      canInstall: false,
      updateReady: false,
      installed: false,
      install: async () => false,
      update: async () => {},
      clientBuild: String(BUILD_INFO?.version || ''),
      serverBuild: null,
    };
  }
  return ctx;
}
